import { UseFormReturn, FieldErrors } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { NewCustomerFormData, ShirtSize, shirtSizeOptions } from "./schemas";
import { useEffect, useMemo, useState } from "react";
import { useListBaseTraders } from "@/lib/api/trader";
import parsePhoneNumber, { AsYouType as PhoneNumberFormatterAsYouType } from 'libphonenumber-js';

interface NewCustomerFormProps {
  form: UseFormReturn<NewCustomerFormData>;
  errors: FieldErrors<NewCustomerFormData>;
  isPreview?: boolean;
  isEmailRequired?: boolean;
}

export function NewCustomerForm({ form, errors, isPreview = false, isEmailRequired = false }: NewCustomerFormProps) {
  const { register, setValue, watch } = form;
  const tradersResult = useListBaseTraders();

  const watchedValues = {
    corn_cfr_check: watch("corn_cfr_check"),
    canola_cfr_check: watch("canola_cfr_check"),
    partner_names: watch("partner_names") || [],
    shirt_sizes: watch("shirt_sizes") || [],
    trader_id: watch("trader_id"),
    trader_name: watch("trader_name"),
    email: watch("email"),
    phone_number: watch("phone_number"),
    mailing_address_line_one: watch("mailing_address_line_one"),
    mailing_address_line_two: watch("mailing_address_line_two"),
    mailing_city: watch("mailing_city"),
    mailing_state: watch("mailing_state"),
    mailing_zipcode: watch("mailing_zipcode"),
    mailing_country: watch("mailing_country"),
    mailing_county: watch("mailing_county"),
    customer_address_line_one: watch("customer_address_line_one"),
    customer_address_line_two: watch("customer_address_line_two"),
    customer_city: watch("customer_city"),
    customer_state: watch("customer_state"),
    customer_zipcode: watch("customer_zipcode"),
    customer_country: watch("customer_country"),
    customer_county: watch("customer_county"),
    bank_institution_name: watch("bank_institution_name"),
    banker_name: watch("banker_name"),
    banker_phone: watch("banker_phone"),
    banker_email: watch("banker_email"),
    has_lienholder: watch("has_lienholder"),
    ach_form_country_type: watch("ach_form_country_type"),
  };

  const [addressesSame, setAddressesSame] = useState(false);

  const handleNumberInput = (field: keyof Pick<NewCustomerFormData, "number_of_acres">) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setValue(field, value === "" ? 0 : parseInt(value, 10));
    }
  };

  // Partner names input - store as raw string, only split on blur
  const [partnerNamesInput, setPartnerNamesInput] = useState("");
  
  // Phone number formatting state
  const [phoneInput, setPhoneInput] = useState("");
  
  // Banker phone number formatting state
  const [bankerPhoneInput, setBankerPhoneInput] = useState("");
  
  // Initialize from form data
  useEffect(() => {
    if (watchedValues.partner_names && watchedValues.partner_names.length > 0) {
      setPartnerNamesInput(watchedValues.partner_names.join(","));
    }
    if (watchedValues.phone_number) {
      setPhoneInput(watchedValues.phone_number);
    }
    if (watchedValues.banker_phone) {
      setBankerPhoneInput(watchedValues.banker_phone);
    }
  }, []);

  const onPartnerNamesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPartnerNamesInput(e.target.value);
  };

  const onPartnerNamesBlur = () => {
    const names = partnerNamesInput
      .split(",")
      .map((n) => n.trim())
      .filter((n) => n.length > 0);
    
    setValue("partner_names", names, { shouldValidate: true, shouldDirty: true });

    const existingSizes = watchedValues.shirt_sizes || [];
    const nextSizes = [...existingSizes];
    if (names.length > existingSizes.length) {
      for (let i = existingSizes.length; i < names.length; i++) {
        nextSizes.push(ShirtSize.Medium);
      }
    } else if (names.length < existingSizes.length) {
      nextSizes.splice(names.length);
    }
    setValue("shirt_sizes", nextSizes, { shouldValidate: true, shouldDirty: true });
  };

  // Phone number formatting handler
  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatter = new PhoneNumberFormatterAsYouType('US');
    const formattedValue = formatter.input(value);
    setPhoneInput(formattedValue);
    setValue("phone_number", formattedValue, { shouldDirty: true, shouldValidate: true });
  };

  // Banker phone number formatting handler
  const handleBankerPhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatter = new PhoneNumberFormatterAsYouType('US');
    const formattedValue = formatter.input(value);
    setBankerPhoneInput(formattedValue);
    setValue("banker_phone", formattedValue, { shouldDirty: true, shouldValidate: true });
  };

  // Convert phone number to E164 format
  const convertPhoneToE164 = (phoneNumber: string): string | null => {
    if (!phoneNumber?.trim()) return null;
    
    const parsedNumber = parsePhoneNumber(phoneNumber, 'US');
    if (parsedNumber?.isValid()) {
      return parsedNumber.format('E.164');
    }
    return null;
  };

  // Default trader to current user if present and trader_id is not set
  useEffect(() => {
    const list = tradersResult?.data?.data || [];
    const current = list.find((t: any) => t.is_current_user);
    if (current && !watchedValues.trader_id && !isPreview) {
      setValue("trader_id", current.id as any, { shouldDirty: true });
      setValue("trader_name", current.name as any, { shouldDirty: true });
    }
  }, [tradersResult?.data?.data, watchedValues.trader_id, isPreview]);

  // Handle address sync when checkbox is checked
  const handleAddressesSameChange = (checked: boolean) => {
    setAddressesSame(checked);
    if (checked) {
      setValue("customer_address_line_one", watchedValues.mailing_address_line_one, { shouldDirty: true });
      setValue("customer_address_line_two", watchedValues.mailing_address_line_two, { shouldDirty: true });
      setValue("customer_city", watchedValues.mailing_city, { shouldDirty: true });
      setValue("customer_state", watchedValues.mailing_state, { shouldDirty: true });
      setValue("customer_zipcode", watchedValues.mailing_zipcode, { shouldDirty: true });
      setValue("customer_country", watchedValues.mailing_country, { shouldDirty: true });
      setValue("customer_county", watchedValues.mailing_county, { shouldDirty: true });
    }
  };

  // Update customer address when mailing address changes and checkbox is checked
  useEffect(() => {
    if (addressesSame) {
      setValue("customer_address_line_one", watchedValues.mailing_address_line_one, { shouldDirty: true });
      setValue("customer_address_line_two", watchedValues.mailing_address_line_two, { shouldDirty: true });
      setValue("customer_city", watchedValues.mailing_city, { shouldDirty: true });
      setValue("customer_state", watchedValues.mailing_state, { shouldDirty: true });
      setValue("customer_zipcode", watchedValues.mailing_zipcode, { shouldDirty: true });
      setValue("customer_country", watchedValues.mailing_country, { shouldDirty: true });
      setValue("customer_county", watchedValues.mailing_county, { shouldDirty: true });
    }
  }, [watchedValues.mailing_address_line_one, watchedValues.mailing_address_line_two, watchedValues.mailing_city, watchedValues.mailing_state, watchedValues.mailing_zipcode, watchedValues.mailing_country, watchedValues.mailing_county, addressesSame]);

  return (
    <div className="space-y-4">
      {/* Basic Customer Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="customer_name">Customer Name *</Label>
          <Input
            id="customer_name"
            {...register("customer_name")}
            placeholder="Enter customer name"
            disabled={isPreview}
            className="w-full"
          />
          {errors.customer_name && (
            <p className="text-sm text-destructive">{errors.customer_name.message}</p>
          )}
        </div>
          <div className="flex items-center space-x-2 mb-1">
            <Checkbox
              id="addresses_same"
              checked={addressesSame}
              onCheckedChange={handleAddressesSameChange}
              disabled={isPreview}
            />
            <Label htmlFor="addresses_same" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Addresses are the same
            </Label>
          </div>
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Mailing Address */}
          <div className="space-y-3">
            <div className="space-y-2 border border-gray-200 rounded-md p-2">
              <Label className="text-base font-semibold">Mailing Address</Label>
              <div className="space-y-1">
                <Label htmlFor="mailing_address_line_one" className="text-sm">Address Line 1</Label>
                <Input
                  id="mailing_address_line_one"
                  {...register("mailing_address_line_one")}
                  placeholder="Street address"
                  disabled={isPreview}
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mailing_address_line_two" className="text-sm">Address Line 2</Label>
                <Input
                  id="mailing_address_line_two"
                  {...register("mailing_address_line_two")}
                  placeholder="Apartment, suite, etc. (optional)"
                  disabled={isPreview}
                  className="w-full"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="mailing_city" className="text-sm">City</Label>
                  <Input
                    id="mailing_city"
                    {...register("mailing_city")}
                    placeholder="City"
                    disabled={isPreview}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mailing_state" className="text-sm">State</Label>
                  <Input
                    id="mailing_state"
                    {...register("mailing_state")}
                    placeholder="State"
                    disabled={isPreview}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mailing_zipcode" className="text-sm">Zipcode</Label>
                  <Input
                    id="mailing_zipcode"
                    {...register("mailing_zipcode")}
                    placeholder="Zipcode"
                    disabled={isPreview}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col md:grid md:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="mailing_county" className="text-sm">County</Label>
                  <Input
                    id="mailing_county"
                    {...register("mailing_county")}
                    placeholder="County"
                    disabled={isPreview}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mailing_country" className="text-sm">Country</Label>
                  <Combobox
                    options={[
                      { value: "USA", label: "USA" },
                      { value: "CANADA", label: "CANADA" }
                    ]}
                    value={watchedValues.mailing_country || ""}
                    onValueChange={(value) => setValue("mailing_country", value as "USA" | "CANADA", { shouldDirty: true })}
                    placeholder="Select country..."
                    className="w-full"
                    popoverClassName="w-full"
                    dwimOnChange
                    isModal
                    disabled={isPreview}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Customer Address (Farm Location) */}
          <div className="space-y-3">
            <div className="space-y-2 border border-gray-200 rounded-md p-2">
              <Label className="text-base font-semibold">Farm Location</Label>
              <div className="space-y-1">
                <Label htmlFor="customer_address_line_one" className="text-sm">Address Line 1</Label>
                <Input
                  id="customer_address_line_one"
                  {...register("customer_address_line_one")}
                  placeholder="Street address"
                  disabled={isPreview || addressesSame}
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="customer_address_line_two" className="text-sm">Address Line 2</Label>
                <Input
                  id="customer_address_line_two"
                  {...register("customer_address_line_two")}
                  placeholder="Apartment, suite, etc. (optional)"
                  disabled={isPreview || addressesSame}
                  className="w-full"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="customer_city" className="text-sm">City</Label>
                  <Input
                    id="customer_city"
                    {...register("customer_city")}
                    placeholder="City"
                    disabled={isPreview || addressesSame}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="customer_state" className="text-sm">State</Label>
                  <Input
                    id="customer_state"
                    {...register("customer_state")}
                    placeholder="State"
                    disabled={isPreview || addressesSame}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="customer_zipcode" className="text-sm">Zipcode</Label>
                  <Input
                    id="customer_zipcode"
                    {...register("customer_zipcode")}
                    placeholder="Zipcode"
                    disabled={isPreview || addressesSame}
                    className="w-full"
                  />
                </div>

              </div>
              <div className="flex flex-col md:grid md:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="customer_county" className="text-sm">County</Label>
                  <Input
                    id="customer_county"
                    {...register("customer_county")}
                    placeholder="County"
                    disabled={isPreview || addressesSame}
                    className="w-full"
                  />
                </div>
              <div className="space-y-1">
                <Label htmlFor="customer_country" className="text-sm">Country</Label>
                <Combobox
                  options={[
                    { value: "USA", label: "USA" },
                    { value: "CANADA", label: "CANADA" }
                  ]}
                  value={watchedValues.customer_country || ""}
                  onValueChange={(value) => setValue("customer_country", value as "USA" | "CANADA", { shouldDirty: true })}
                  placeholder="Select country..."
                  className="w-full"
                  popoverClassName="w-full"
                  dwimOnChange
                  isModal
                  disabled={isPreview || addressesSame}
                />
              </div>
              </div>
            </div>
          </div>
          </div>
          </div>

      {/* Business Information */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-1">
          <Label htmlFor="number_of_acres">Number of Acres</Label>
          <Input
            id="number_of_acres"
            type="number"
            {...register("number_of_acres", { valueAsNumber: true })}
            placeholder="Enter number of acres"
            onChange={handleNumberInput("number_of_acres")}
            disabled={isPreview}
            className="w-full"
          />
          {errors.number_of_acres && (
            <p className="text-sm text-destructive">{errors.number_of_acres.message}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="bol_sequence">BOL Sequence</Label>
          <Input
            id="bol_sequence"
            {...register("bol_sequence")}
            placeholder="Enter BOL sequence"
            disabled={isPreview}
            className="w-full"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="phone_number">Phone Number</Label>
          <Input
            id="phone_number"
            type="tel"
            value={phoneInput}
            onChange={handlePhoneInputChange}
            placeholder="(555) 123-4567"
            disabled={isPreview}
            className="w-full"
          />
          {errors.phone_number && (
            <p className="text-sm text-destructive">{errors.phone_number.message}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="email">Email {isEmailRequired ? "*" : ""}</Label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            placeholder="name@example.com"
            disabled={isPreview}
            className="w-full"
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>
      </div>

      {/* CFR Requirements */}
      <div className="flex flex-wrap gap-6">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="corn_cfr_check"
            checked={watchedValues.corn_cfr_check}
            onCheckedChange={(checked) => setValue("corn_cfr_check", !!checked)}
            disabled={isPreview}
          />
          <Label htmlFor="corn_cfr_check" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Corn CFR Needed
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="canola_cfr_check"
            checked={watchedValues.canola_cfr_check}
            onCheckedChange={(checked) => setValue("canola_cfr_check", !!checked)}
            disabled={isPreview}
          />
          <Label htmlFor="canola_cfr_check" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Canola CFR Needed
          </Label>
        </div>
      </div>

      {/* Partners and Shirt Sizes */}
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="partner_names">Partners (comma separated)</Label>
          <Input
            id="partner_names"
            value={partnerNamesInput}
            onChange={onPartnerNamesChange}
            onBlur={onPartnerNamesBlur}
            placeholder="Jane Doe, John Smith, ..."
            disabled={isPreview}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">Total partners: {(watchedValues.partner_names || []).length}</p>
          {errors.partner_names && (
            <p className="text-sm text-destructive">{(errors as any).partner_names?.message as string}</p>
          )}
        </div>

        {(watchedValues.partner_names || []).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(watchedValues.partner_names || []).map((name: string, idx: number) => (
              <div className="space-y-1" key={`shirt-size-${idx}`}>
                <Label htmlFor={`shirt_size_${idx}`}>Shirt Size ({name || `Partner ${idx+1}`})</Label>
                <Combobox
                  options={shirtSizeOptions}
                  value={(watchedValues.shirt_sizes || [])[idx]}
                  onValueChange={(value) => {
                    const next = [...(watchedValues.shirt_sizes || [])];
                    next[idx] = value as ShirtSize;
                    setValue("shirt_sizes", next, { shouldValidate: true, shouldDirty: true });
                  }}
                  placeholder="Select shirt size..."
                  className="w-full"
                  popoverClassName="w-full"
                  dwimOnChange
                  isModal
                  disabled={isPreview}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bank and Lienholder Information */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="bank_institution_name">Bank Institution Name {watchedValues.has_lienholder ? "*" : ""}</Label>
            <Input
              required={!!watchedValues.has_lienholder}
              id="bank_institution_name"
              {...register("bank_institution_name")}
              placeholder="Enter bank institution name"
              disabled={isPreview}
              className="w-full"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="banker_name">Banker's Name</Label>
            <Input
              id="banker_name"
              {...register("banker_name")}
              placeholder="Enter banker's name"
              disabled={isPreview}
              className="w-full"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="banker_phone">Banker Phone {watchedValues.has_lienholder ? "*" : ""}</Label>
            <Input
              required={!!watchedValues.has_lienholder}
              id="banker_phone"
              type="tel"
              value={bankerPhoneInput}
              onChange={handleBankerPhoneInputChange}
              placeholder="(555) 123-4567"
              disabled={isPreview}
              className="w-full"
            />
            {errors.banker_phone && (
              <p className="text-sm text-destructive">{errors.banker_phone.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="banker_email">Banker Email {watchedValues.has_lienholder ? "*" : ""}</Label>
            <Input
              required={!!watchedValues.has_lienholder}
              id="banker_email"
              type="email"
              {...register("banker_email")}
              placeholder="banker@example.com"
              disabled={isPreview}
              className="w-full"
            />
            {errors.banker_email && (
              <p className="text-sm text-destructive">{errors.banker_email.message}</p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="has_lienholder"
            checked={watchedValues.has_lienholder}
            onCheckedChange={(checked) => setValue("has_lienholder", !!checked)}
            disabled={isPreview}
          />
          <Label htmlFor="has_lienholder" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Has Lienholder
          </Label>
        </div>

        {/* ACH Form Country Type */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="ach_form_country_type"
            checked={watchedValues.ach_form_country_type === "CAD"}
            onCheckedChange={(checked) => {
              setValue("ach_form_country_type", checked ? "CAD" : "USA", { shouldDirty: true, shouldValidate: true });
            }}
            disabled={isPreview}
          />
          <Label htmlFor="ach_form_country_type" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            CAD
          </Label>
        </div>
        {errors.ach_form_country_type && (
          <p className="text-sm text-destructive">{errors.ach_form_country_type.message}</p>
        )}
      </div>

      {/* Trader Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="trader_id">Trader</Label>
          <Combobox
            isModal
            dwimOnChange
            className="w-full"
            id="trader_id"
            value={watchedValues.trader_id || ""}
            onValueChange={(value) => {
              const trader_name = (tradersResult?.data?.data || []).find((t: any) => t.id === value)?.name || "";
              setValue("trader_id", value as any, { shouldDirty: true })
              setValue("trader_name", trader_name as any, { shouldDirty: true })
            }}
            options={(tradersResult?.data?.data || []).map((t: any) => ({ value: t.id, label: t.name }))}
            disabled={isPreview}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="customer_contact_info">Contact Info *</Label>
          <Textarea
            id="customer_contact_info"
            {...register("customer_contact_info")}
            placeholder="Enter contact information (phone, email, etc.)"
            rows={2}
            disabled={isPreview}
            className="w-full resize-none"
          />
          {errors.customer_contact_info && (
            <p className="text-sm text-destructive">{errors.customer_contact_info.message}</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-1">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Enter any additional notes"
          rows={3}
          disabled={isPreview}
          className="w-full resize-none"
        />
        {errors.notes && (
          <p className="text-sm text-destructive">{errors.notes.message}</p>
        )}
      </div>

      {/* Hidden Schema Version Fields */}
      <input type="hidden" {...register("schema_version")} value="v2" />
      <input type="hidden" {...register("schema_type")} value="new_customer_form" />
    </div>
  );
}


